// components/layout/MainLayout.jsx
import NotificationsIcon from "@mui/icons-material/Notifications";
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from "@mui/material";
import { Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { images } from "../../assets";
import { getNotificationsByType, marksAllRead } from "../../services/Notification";
import useUserStore from "../../zustand/useUserStore";
import useWorkerStore from "../../zustand/useWorkerStore";
import TextInput from "../textInput";
import NotificationMenu from "./menuNotification";
import Sidebar, { drawerWidth } from "./sidebar";

const MainLayout = ({ children }) => {
  const { user, clearUserData } = useUserStore();
  const { initializeData } = useWorkerStore();
  const [open, setOpen] = useState(true);
  const [activeItem, setActiveItem] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const notificationMenuOpen = Boolean(notificationAnchorEl);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const gradient = "linear-gradient(to bottom, #2e7d32, #66bb6a)";
  const iconStyle = {
    color: gradient,
    padding: "10px",
  };
  // const userData = JSON.parse(localStorage.getItem("user_data_web"));

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 700) {
        setOpen(false);
      } else {
        setOpen(true);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Auto-initialize workers data when MainLayout mounts
  useEffect(() => {
    initializeData();
  }, [initializeData]);

  // Fetch notifications to compute unread count (only isRead === false)
  const fetchNotifications = async () => {
    try {
      const response = await getNotificationsByType();
      if (response?.status === 200 || response?.status === 201) {
        const list = response.data.data || [];
        // Filter: only notifications with senderRole (worker or admin)
        const filteredList = list.filter(
          (item) => item.senderRole && (item.senderRole === "worker" || item.senderRole === "admin")
        );
        const unreadNotifications = filteredList.filter((item) => item.isRead === false);
        setUnreadCount(unreadNotifications.length);
      }
    } catch (e) {
      console.log("Failed to load notifications for badge", e);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    clearUserData();
    navigate("/login");
  };

  const handleNotificationClick = async (event) => {
    setNotificationAnchorEl(event.currentTarget);
    
    // Mark all notifications as read when icon is clicked
    try {
      const response = await marksAllRead();
      if (response?.status === 200 || response?.status === 201) {
        // Refresh unread count after marking as read
        fetchNotifications();
      }
    } catch (error) {
      console.error("Error marking notifications as read:", error);
    }
  };

  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };

  return (
    <Box sx={{ display: "flex", backgroundColor: "#fff" }}>
      <Sidebar
        open={open}
        setOpen={setOpen}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
      />

      <AppBar
        position="fixed"
        sx={{
          background: "#fff",
          color: "primary.main",
          width: `calc(100% - ${open ? drawerWidth : 120}px)`,
          ml: `${open ? drawerWidth : 120}px`,
          transition: "margin-left 0.3s, width 0.3s",
          boxShadow: "0",
          padding: "18px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            justifyContent: "space-between",
            p: 2,
            backgroundColor: "#eee",
            borderRadius: "22px",
            width: "100%",
          }}
        >
          {/* {location.pathname === "/employee-management" && (
            <Box width={"40%"}>
              <TextInput
                placeholder="Search Employee"
                inputBgColor="#ffff"
                // value={search}
                // onChange={handleChange}
                fullWidth
                sx={{ width: "100%" }}
                borderRadius={"22px"}
                padding={"5px"}
                InputStartIcon={<Search size={20} />}
              />
            </Box>
          )} */}

          <Box
            display={"flex"}
            justifyContent={"flex-end"}
            width={"100%"}
            alignItems={"center"}
            gap={2}
            p={1}
          >
            <IconButton
              sx={{ background: "#fff" }}
              onClick={(e) => {
                handleNotificationClick(e);
              }}
            >
              <Badge
                color="error"
                overlap="circular"
                variant="dot"
                invisible={!unreadCount}
              >
                <NotificationsIcon sx={{ color: "#2e7d32" }} />
              </Badge>
            </IconButton>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                cursor: "pointer",
                transition: "transform 0.2s ease",
                "&:hover": {
                  transform: "scale(1.05)",
                },
              }}
              src={images.man}
              onClick={handleClick}
            >
              {"U"}
            </Avatar>
            <Box>
              <Typography color="#000" fontSize={12} fontWeight={600}>
                {user?.username}
              </Typography>
              <Typography color="#6e6a6a" fontSize={10}>
                {user?.email}
              </Typography>
            </Box>
          </Box>
        </Box>
      </AppBar>

      <Menu
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          sx: {
            mt: 0.5,
            width: 280,
            borderRadius: 2,
            boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
            padding: "8px 0",
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Box
          sx={{
            p: "12px 16px",
            textAlign: "center",
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Avatar
            sx={{
              width: 100,
              height: 100,
              cursor: "pointer",
              transition: "transform 0.2s ease",
              "&:hover": {
                transform: "scale(1.05)",
              },
            }}
            // src={adminIcons.profile}
          >
            {"U"}
          </Avatar>

          <Typography
            sx={{ fontSize: 16, fontWeight: 500, color: "#2C2C2C", mt: "20px" }}
          >
            Admin
          </Typography>
        </Box>
        <Divider />
        <MenuItem
          sx={{ fontSize: 14, py: 1, px: 2 }}
          onClick={() => navigate("/settings")}
        >
          {" "}
          <img
            // src={adminIcons.setting}
            height={25}
            style={{ marginRight: "4px" }}
          />
          Settings
        </MenuItem>
        <MenuItem onClick={handleLogout} sx={{ fontSize: 14, py: 1, px: 2 }}>
          <img
            // src={adminIcons.logout}
            height={25}
            style={{ marginRight: "4px" }}
          />{" "}
          Log Out
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={notificationAnchorEl}
        open={notificationMenuOpen}
        onClose={handleNotificationClose}
        PaperProps={{
          sx: {
            mt: 1.5,
            width: 250,
            borderRadius: 2,
            boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
            padding: 0,
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <NotificationMenu />
      </Menu>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 2,
          width: `calc(100% - ${open ? drawerWidth : 120}px)`,
          transition: "margin-left 0.3s, width 0.3s",
          backgroundColor: "#fff",
          mt: 6,
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default MainLayout;
